import { ChangeDetectorRef, Component, ElementRef, Inject, Input, OnInit } from '@angular/core';
import { ApplicationFormService } from '../application-form/application-form.service';
import { LoggerService } from '@app/blocks/logger/services/logger.service';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { Observable, Subject, firstValueFrom, of, takeUntil } from 'rxjs';
import { DataService } from '@app/core/services/data.service';
import { formatFormErrors } from '@app/shared/formly/form-config';
import { ApplicationFormDataService } from '../application-form/application-form-data.service';
import { SlideCommonService } from '../common/slide-common.service';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { ConnectSlideComponent } from '@app/blocks/ui/slide/slide.component';
import * as _ from 'lodash';
import { WINDOW, WINDOW_PROVIDERS } from '@app/shared/services/window/window.service';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner.component';

@Component({
    selector: 'app-affordability',
    templateUrl: './affordability.component.html',
    providers: [ WINDOW_PROVIDERS ],
    imports: [
        CommonModule,
        ConnectSlideComponent,
        SpinnerComponent,
        ReactiveFormsModule,
        NgIf,
        NgFor
    ]
})
export class AffordabilityComponent implements OnInit {
    @Input() slideNumber: string | undefined;
    @Input() finalSlide = false;
    currentSlide = 'affordability';
    active = this.afs.getActive();
    user = this.afs.getAffordabilityModel();
    showError = false;
    showFormErrors = false; //error summary
    showSubmit = false;
    errorMessage = "";
    errors: any = [];
    isDisabled = false;
    submitting = false;

    affordabilityDataLoaded = !_.isEmpty(this.user);

    affordabilityForm: FormGroup = this.fb.group({
        incomes: this.fb.array([]),
        outgoings: this.fb.array([])
    });

    constructor(
        private afs: ApplicationFormService,
        private logger: LoggerService,
        private el: ElementRef,
        private dataService: DataService,
        private applicationFormDataService: ApplicationFormDataService,
        private cdr: ChangeDetectorRef,
        private slideCommon: SlideCommonService,
        private fb: FormBuilder,
        @Inject(WINDOW) private window: Window
    ) {
    }

    private destroy$ = new Subject<void>();

    subscription = this.afs.subscribeAffordabilityModel((data: any) => {
        this.user = data;
        console.log(data);
        this.affordabilityDataLoaded = !_.isEmpty(this.user);

        const observables: Observable<any>[] = [
            of(this.afs.getAffordabilityModel()),
        ]

        this.dataService.ready(observables).pipe(takeUntil(this.destroy$)).subscribe(
            {
                next: () => {
                    this.showSubmit = true;

                    this.initIncomeControls();
                },
                error: () => {
                    console.log('"ready" function failed');
                }
            }
        );
    });

    initIncomeControls() {
        //reset state
        this.affordabilityForm = this.fb.group({
            incomes: this.fb.array([]),
            outgoings: this.fb.array([]),
        },
            { validators: [ this.incomesMinValidator, this.outgoingsMinValidator ] }
        );

        this.user.incomes.forEach((income: any) => {
            this.incomes.push(this.createIncomeExpenditure(income));
        });

        this.user.outgoings.forEach((outgoing: any) => {
            this.outgoings.push(this.createIncomeExpenditure(outgoing));
        });

        this.affordabilityForm.updateValueAndValidity();
    }

    createIncomeExpenditure(item?: any) {
        return this.fb.group({
            type: [ item.type ],
            label: [ item.label ],
            status: [ item.status ],
            amount: [ item.amount ],
            frequency: [ item.frequency ]
        });
    }

    get formState() {
        return this.afs.formState;
    }

    ngOnInit() {
        console.log('affordability');
        this.afs.getActive();
    }

    onBack() {
        this.isDisabled = false;
        this.afs.goBack('affordability');

        this.slideCommon.scroll(this.active.active)
    }

    onSubmit() {
        this.incomes.controls.forEach(control => {
            if (control.value.status === 'active') {
                control.markAllAsTouched();

            }
        })
        this.outgoings.controls.forEach(control => {
            if (control.value.status === 'active') {
                control.markAllAsTouched();
            }
        })

        this.affordabilityForm.updateValueAndValidity();

        this.logger.info('affordability submit clicked');

        if (this.affordabilityForm.valid) {
            this.errors = [];
            this.showFormErrors = false;
            this.isDisabled = true;
            this.submitting = true;

            firstValueFrom(this.applicationFormDataService.postAffordability(this.affordabilityForm.value, this.afs.getLeadModel().applicationId))
                .then((response) => {
                    console.log('affordability post success ' + response.status);

                    this.slideCommon.save(this.affordabilityForm, this.currentSlide, this.formState, this.active);

                    this.isDisabled = false;
                    this.submitting = false;
                })
                .catch((error) => {
                    this.logger.error('postAffordability error', error.data);
                    this.showError = true;
                    this.submitting = false;
                    this.isDisabled = false;
                    this.errorMessage = 'Error saving your affordability details, please contact support.';
                    return null;
                })
        }
        else {

            const renderTime = 500;
            const revealTime = renderTime + 250;

            setTimeout(() => {
                this.errors = formatFormErrors(this.el, this.affordabilityForm);
                this.showFormErrors = true;
            }, renderTime);

            setTimeout(() => {
                this.scrollToId('affordability-error-summary');
            }, revealTime);
        }

        console.log(this.affordabilityForm.errors)

        this.errors = [];
    }

    scrollToId(id: string) {
        this.slideCommon.scrollToId(id, this.el, this.window);
    }

    add(control: AbstractControl, mode?: string) {
        if (mode === 'incomes') {
            this.moveControlToEnd(control, this.incomes);
        }
        else {
            this.moveControlToEnd(control, this.outgoings);
        }

        control.get('amount')?.addValidators(Validators.required);
        control.get('frequency')?.addValidators(Validators.required);

        this.affordabilityForm.updateValueAndValidity();

        control.patchValue({
            status: 'active'
        });
    }

    remove(control: AbstractControl, mode?: string) {
        if (mode === 'incomes') {
            this.moveControlToEnd(control, this.incomes);
        }
        else {
            this.moveControlToEnd(control, this.outgoings);
        }

        control.get('amount')?.clearValidators();
        control.get('frequency')?.clearValidators();

        this.affordabilityForm.updateValueAndValidity();

        control.patchValue({
            status: 'inactive',
            amount: null,
            frequency: null
        });
    }

    moveControlToEnd(control: AbstractControl, array: FormArray) {
        const index = array.controls.indexOf(control);

        if (index !== -1) {
            array.insert(array.controls.length, control);
            array.removeAt(index);
        }
    }

    incomesMinValidator: ValidatorFn = (control: AbstractControl): { [ key: string ]: any } | null => {
        const membersArray = control.get('incomes') as FormArray;

        let activeCount = 0;

        membersArray.controls.forEach((control: AbstractControl) => {
            if (control.value.status === 'active') {
                activeCount++;
            }
        })

        if (activeCount > 0) {
            return null;
        } else {
            return {
                'incomesMin': {
                    incomesMin: true, title: 'Income', msg: 'At least one income item is required.'
                }
            };
        }
    };

    outgoingsMinValidator: ValidatorFn = (control: AbstractControl): { [ key: string ]: any } | null => {
        const membersArray = control.get('outgoings') as FormArray;

        let activeCount = 0;

        membersArray.controls.forEach((control: AbstractControl) => {
            if (control.value.status === 'active') {
                activeCount++;
            }
        })

        if (activeCount > 0) {
            return null;
        } else {
            return {
                'outgoingsMin': {
                    outGoingsMin: true, title: 'Outgoings', msg: 'At least one outgoings item is required.'
                }
            };
        }
    };

    // helpers
    get incomes() {
        return this.affordabilityForm.get('incomes') as FormArray;
    }

    get outgoings() {
        return this.affordabilityForm.get('outgoings') as FormArray;
    }
}
